import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Stage, Layer, Rect, Circle, Text, Image as KonvaImage, Transformer, Group } from 'react-konva';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Slider } from '../ui/slider';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import {
  Type, Square, Circle as CircleIcon, Image, Move, MousePointer,
  Undo2, Redo2, ZoomIn, ZoomOut, Download, Save, Copy, Trash2,
  Lock, Unlock, Eye, EyeOff, ChevronUp, ChevronDown, Palette,
  AlignLeft, AlignCenter, AlignRight, Bold, Italic, Layers,
  Grid3X3, Settings2, Plus, FolderOpen, RefreshCcw
} from 'lucide-react';
import AssetPicker from './AssetPicker';
import { useBrandSettings } from '../../hooks/useBrandSettings';

// Design preset dimensions
const DESIGN_PRESETS = {
  facebook_post: { width: 1200, height: 628, name: 'Facebook Post' },
  instagram_post: { width: 1080, height: 1080, name: 'Instagram Post' },
  instagram_story: { width: 1080, height: 1920, name: 'Instagram Story' },
  ad_square: { width: 1080, height: 1080, name: 'Ad Square' },
  thumbnail: { width: 1280, height: 720, name: 'YouTube Thumbnail' }
};

// Default brand colors (fallback if hook fails)
const DEFAULT_BRAND_COLORS = [
  '#1E3A8A', '#3B82F6', '#EAB308', '#000000', '#FFFFFF',
  '#22C55E', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4'
];

// Font options
const FONT_OPTIONS = [
  'Inter', 'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 
  'Verdana', 'Trebuchet MS', 'Palatino'
];

// Tool types
const TOOLS = {
  SELECT: 'select',
  TEXT: 'text',
  RECT: 'rect',
  CIRCLE: 'circle',
  IMAGE: 'image'
};

// DesignEditor Component
const DesignEditor = ({
  design,
  onSave,
  onExport,
  onSchedule,
  brandColors: propBrandColors,
  readOnly = false
}) => {
  // Phase 6: Fetch brand colors from Admin Company Settings
  const { brandSettings, loading: brandLoading, refetch: refetchBrand } = useBrandSettings();
  
  // Use prop colors if provided, otherwise use Admin settings, fallback to defaults
  const brandColors = propBrandColors || brandSettings?.brand_colors || DEFAULT_BRAND_COLORS;
  
  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  // Canvas state
  const [elements, setElements] = useState(design?.elements || []);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  
  // Use a ref to track if we've initialized from design
  const hasInitialized = useRef(false);
  const designId = design?.id;
  
  // Canvas dimensions
  const [canvasWidth, setCanvasWidth] = useState(design?.canvas_width || 1200);
  const [canvasHeight, setCanvasHeight] = useState(design?.canvas_height || 628);
  const [canvasBackground, setCanvasBackground] = useState(design?.canvas_background || '#FFFFFF');
  
  // Viewport
  const [stageScale, setStageScale] = useState(0.5);
  const [stagePos, setStagePos] = useState({ x: 50, y: 50 });
  
  // History for undo/redo
  const [history, setHistory] = useState([elements]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Snap guides
  const [guides, setGuides] = useState({ vertical: [], horizontal: [] });
  const [showGrid, setShowGrid] = useState(false);
  
  // Asset Picker state
  const [showAssetPicker, setShowAssetPicker] = useState(false);

  // Initialize from design prop when design changes (only on new design load)
  useEffect(() => {
    if (design && design.id !== hasInitialized.current) {
      hasInitialized.current = design.id;
      // Use a microtask to avoid cascading renders
      queueMicrotask(() => {
        setElements(design.elements || []);
        setCanvasWidth(design.canvas_width || 1200);
        setCanvasHeight(design.canvas_height || 628);
        setCanvasBackground(design.canvas_background || '#FFFFFF');
        setHistory([design.elements || []]);
        setHistoryIndex(0);
      });
    }
  }, [designId, design]);

  // Update transformer when selection changes
  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;
    
    const stage = stageRef.current;
    const transformer = transformerRef.current;
    
    if (selectedId) {
      const selectedNode = stage.findOne(`#${selectedId}`);
      if (selectedNode) {
        transformer.nodes([selectedNode]);
        transformer.getLayer()?.batchDraw();
      }
    } else {
      transformer.nodes([]);
      transformer.getLayer()?.batchDraw();
    }
  }, [selectedId, elements]);

  // Save to history
  const saveHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newElements);
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(history[historyIndex - 1]);
      setSelectedId(null);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(history[historyIndex + 1]);
      setSelectedId(null);
    }
  }, [history, historyIndex]);

  // Add element
  const addElement = useCallback((type, props = {}) => {
    const newElement = {
      id: uuidv4(),
      type,
      x: canvasWidth / 2 - 50,
      y: canvasHeight / 2 - 25,
      width: type === 'text' ? 200 : 100,
      height: type === 'text' ? 50 : 100,
      rotation: 0,
      fill_color: type === 'text' ? '#000000' : '#3B82F6',
      stroke_color: null,
      stroke_width: 0,
      opacity: 1,
      z_index: elements.length,
      locked: false,
      visible: true,
      // Text-specific
      text: type === 'text' ? 'Add your text' : null,
      font_family: 'Inter',
      font_size: 32,
      font_weight: 'normal',
      font_style: 'normal',
      text_align: 'center',
      // Shape-specific
      shape_type: type === 'rect' ? 'rect' : type === 'circle' ? 'circle' : null,
      // Image-specific
      image_url: null,
      ...props
    };
    
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveHistory(newElements);
    setSelectedId(newElement.id);
    setActiveTool(TOOLS.SELECT);
  }, [elements, canvasWidth, canvasHeight, saveHistory]);

  // Update element
  const updateElement = useCallback((id, updates) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    );
    setElements(newElements);
    saveHistory(newElements);
  }, [elements, saveHistory]);

  // Delete element
  const deleteElement = useCallback((id) => {
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    saveHistory(newElements);
    if (selectedId === id) setSelectedId(null);
  }, [elements, selectedId, saveHistory]);

  // Duplicate element
  const duplicateElement = useCallback((id) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;
    
    const newElement = {
      ...element,
      id: uuidv4(),
      x: element.x + 20,
      y: element.y + 20,
      z_index: elements.length
    };
    
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveHistory(newElements);
    setSelectedId(newElement.id);
  }, [elements, saveHistory]);

  // Move layer
  const moveLayer = useCallback((id, direction) => {
    const index = elements.findIndex(el => el.id === id);
    if (index === -1) return;
    
    const newIndex = direction === 'up' 
      ? Math.min(index + 1, elements.length - 1)
      : Math.max(index - 1, 0);
    
    if (newIndex === index) return;
    
    const newElements = [...elements];
    const [element] = newElements.splice(index, 1);
    newElements.splice(newIndex, 0, element);
    
    // Update z_index for all elements
    newElements.forEach((el, i) => el.z_index = i);
    
    setElements(newElements);
    saveHistory(newElements);
  }, [elements, saveHistory]);

  // Handle image upload from file input
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      addElement('image', { image_url: reader.result, width: 200, height: 200 });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [addElement]);

  // Handle asset selection from Asset Picker
  const handleAssetSelect = useCallback((asset) => {
    // Asset comes with: id, url, fullUrl, name, asset_type, etc.
    const imageUrl = asset.fullUrl || asset.url;
    
    // Create image element with asset reference
    addElement('image', { 
      image_url: imageUrl, 
      asset_id: asset.id,  // Store asset reference
      width: asset.width || 200, 
      height: asset.height || 200 
    });
    
    toast.success(`Added "${asset.name}" to canvas`);
  }, [addElement]);

  // Handle stage click (deselect or add element)
  const handleStageClick = useCallback((e) => {
    if (e.target === e.target.getStage() || e.target.name() === 'canvas-background') {
      setSelectedId(null);
      
      // Add element if tool is active
      if (activeTool === TOOLS.TEXT) {
        const pos = e.target.getStage().getPointerPosition();
        const scale = stageScale;
        addElement('text', { 
          x: (pos.x - stagePos.x) / scale, 
          y: (pos.y - stagePos.y) / scale 
        });
      } else if (activeTool === TOOLS.RECT) {
        const pos = e.target.getStage().getPointerPosition();
        const scale = stageScale;
        addElement('rect', { 
          x: (pos.x - stagePos.x) / scale, 
          y: (pos.y - stagePos.y) / scale 
        });
      } else if (activeTool === TOOLS.CIRCLE) {
        const pos = e.target.getStage().getPointerPosition();
        const scale = stageScale;
        addElement('circle', { 
          x: (pos.x - stagePos.x) / scale, 
          y: (pos.y - stagePos.y) / scale 
        });
      }
    }
  }, [activeTool, addElement, stageScale, stagePos]);

  // Handle zoom
  const handleZoom = useCallback((delta) => {
    const newScale = Math.max(0.1, Math.min(2, stageScale + delta));
    setStageScale(newScale);
  }, [stageScale]);

  // Export to PNG
  const handleExport = useCallback(() => {
    if (!stageRef.current) return;
    
    // Temporarily hide transformer and deselect
    const prevSelected = selectedId;
    setSelectedId(null);
    
    setTimeout(() => {
      const uri = stageRef.current.toDataURL({
        x: 0,
        y: 0,
        width: canvasWidth,
        height: canvasHeight,
        pixelRatio: 2
      });
      
      // Download
      const link = document.createElement('a');
      link.download = `${design?.name || 'design'}.png`;
      link.href = uri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Design exported!');
      setSelectedId(prevSelected);
      
      if (onExport) onExport(uri);
    }, 100);
  }, [canvasWidth, canvasHeight, design?.name, selectedId, onExport]);

  // Get current design data
  const getDesignData = useCallback(() => ({
    elements,
    canvas_width: canvasWidth,
    canvas_height: canvasHeight,
    canvas_background: canvasBackground
  }), [elements, canvasWidth, canvasHeight, canvasBackground]);

  // Handle save
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(getDesignData());
    }
  }, [onSave, getDesignData]);

  // Handle schedule
  const handleSchedule = useCallback(() => {
    if (onSchedule) {
      // First export a preview
      if (stageRef.current) {
        setSelectedId(null);
        setTimeout(() => {
          const preview = stageRef.current.toDataURL({
            x: 0,
            y: 0,
            width: canvasWidth,
            height: canvasHeight,
            pixelRatio: 1
          });
          onSchedule(getDesignData(), preview);
        }, 100);
      } else {
        onSchedule(getDesignData(), null);
      }
    }
  }, [onSchedule, getDesignData, canvasWidth, canvasHeight]);

  // Selected element
  const selectedElement = selectedId ? elements.find(el => el.id === selectedId) : null;

  // Render element based on type
  const renderElement = (element) => {
    // Common drag end handler
    const handleDragEnd = (e) => {
      updateElement(element.id, {
        x: e.target.x(),
        y: e.target.y()
      });
    };

    // Transform handler for shapes (rect, circle, image) - scales width/height
    const handleShapeTransformEnd = (e) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      
      // Reset scale and apply to dimensions
      node.scaleX(1);
      node.scaleY(1);
      
      updateElement(element.id, {
        x: node.x(),
        y: node.y(),
        width: Math.max(5, node.width() * scaleX),
        height: Math.max(5, node.height() * scaleY),
        rotation: node.rotation()
      });
    };

    // Transform handler for text - adjusts width for wrapping, not scaling
    const handleTextTransformEnd = (e) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      
      // Calculate new width based on horizontal scale
      const newWidth = Math.max(20, node.width() * scaleX);
      
      // For text, we adjust width for wrapping but don't stretch the text visually
      // If user scales vertically, we can optionally adjust font size proportionally
      // But to prevent distortion, we keep font size stable during width-only resizes
      let newFontSize = element.font_size || 24;
      
      // Only adjust font size if user is doing a proportional scale (corner drag)
      // Check if both scales are roughly equal (within 10% tolerance)
      const isProportionalScale = Math.abs(scaleX - scaleY) < 0.1 * Math.max(scaleX, scaleY);
      
      if (isProportionalScale && scaleX !== 1) {
        // Proportional resize: scale font size
        newFontSize = Math.max(8, Math.round(element.font_size * scaleX));
      }
      // For non-proportional (side drag): only adjust width, keep font size
      
      // Reset node scale to prevent visual stretching
      node.scaleX(1);
      node.scaleY(1);
      
      updateElement(element.id, {
        x: node.x(),
        y: node.y(),
        width: newWidth,
        font_size: newFontSize,
        rotation: node.rotation()
      });
    };

    const commonProps = {
      id: element.id,
      key: element.id,
      x: element.x,
      y: element.y,
      rotation: element.rotation,
      opacity: element.opacity,
      visible: element.visible,
      draggable: !element.locked && !readOnly,
      onClick: () => setSelectedId(element.id),
      onTap: () => setSelectedId(element.id),
      onDragEnd: handleDragEnd
    };

    switch (element.type) {
      case 'text':
        return (
          <Text
            {...commonProps}
            text={element.text || 'Text'}
            fontSize={element.font_size}
            fontFamily={element.font_family}
            fontStyle={`${element.font_weight} ${element.font_style}`.trim()}
            fill={element.fill_color}
            align={element.text_align}
            width={element.width}
            wrap="word"
            onTransformEnd={handleTextTransformEnd}
          />
        );
      
      case 'rect':
        return (
          <Rect
            {...commonProps}
            width={element.width}
            height={element.height}
            fill={element.fill_color}
            stroke={element.stroke_color}
            strokeWidth={element.stroke_width}
            cornerRadius={4}
            onTransformEnd={handleShapeTransformEnd}
          />
        );
      
      case 'circle':
        return (
          <Circle
            {...commonProps}
            radius={Math.min(element.width, element.height) / 2}
            fill={element.fill_color}
            stroke={element.stroke_color}
            strokeWidth={element.stroke_width}
            onTransformEnd={handleShapeTransformEnd}
          />
        );
      
      case 'image':
        if (!element.image_url) return null;
        return (
          <ImageElement
            {...commonProps}
            src={element.image_url}
            width={element.width}
            height={element.height}
            onTransformEnd={handleShapeTransformEnd}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full bg-slate-100 dark:bg-slate-900" data-testid="design-editor">
      {/* Left Panel - Tools & Layers */}
      <div className="w-64 bg-white dark:bg-slate-800 border-r dark:border-slate-700 flex flex-col">
        {/* Tools */}
        <div className="p-3 border-b dark:border-slate-700">
          <Label className="text-xs font-medium text-muted-foreground mb-2 block">TOOLS</Label>
          <div className="grid grid-cols-5 gap-1">
            <Button
              variant={activeTool === TOOLS.SELECT ? 'default' : 'ghost'}
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => setActiveTool(TOOLS.SELECT)}
              title="Select (V)"
              data-testid="tool-select"
            >
              <MousePointer className="h-4 w-4" />
            </Button>
            <Button
              variant={activeTool === TOOLS.TEXT ? 'default' : 'ghost'}
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => setActiveTool(TOOLS.TEXT)}
              title="Text (T)"
              data-testid="tool-text"
            >
              <Type className="h-4 w-4" />
            </Button>
            <Button
              variant={activeTool === TOOLS.RECT ? 'default' : 'ghost'}
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => setActiveTool(TOOLS.RECT)}
              title="Rectangle (R)"
              data-testid="tool-rect"
            >
              <Square className="h-4 w-4" />
            </Button>
            <Button
              variant={activeTool === TOOLS.CIRCLE ? 'default' : 'ghost'}
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => setActiveTool(TOOLS.CIRCLE)}
              title="Circle (C)"
              data-testid="tool-circle"
            >
              <CircleIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0"
              onClick={() => fileInputRef.current?.click()}
              title="Upload Image (I)"
              data-testid="tool-image"
            >
              <Image className="h-4 w-4" />
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
          
          {/* Asset Library Button */}
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-2"
            onClick={() => setShowAssetPicker(true)}
            data-testid="open-asset-picker"
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            Asset Library
          </Button>
        </div>

        {/* Layers Panel */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-3 border-b dark:border-slate-700">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-2">
              <Layers className="h-3 w-3" /> LAYERS ({elements.length})
            </Label>
          </div>
          <ScrollArea className="flex-1 p-2">
            <div className="space-y-1">
              {[...elements].reverse().map((el, index) => (
                <div
                  key={el.id}
                  className={`
                    flex items-center gap-2 p-2 rounded text-xs cursor-pointer
                    ${selectedId === el.id 
                      ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-500' 
                      : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                    }
                  `}
                  onClick={() => setSelectedId(el.id)}
                  data-testid={`layer-${el.id}`}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateElement(el.id, { visible: !el.visible });
                    }}
                  >
                    {el.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateElement(el.id, { locked: !el.locked });
                    }}
                  >
                    {el.locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                  </Button>
                  <span className="flex-1 truncate dark:text-white">
                    {el.type === 'text' ? (el.text?.substring(0, 15) || 'Text') : el.type}
                  </span>
                  <div className="flex gap-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={(e) => { e.stopPropagation(); moveLayer(el.id, 'up'); }}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={(e) => { e.stopPropagation(); moveLayer(el.id, 'down'); }}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {elements.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-xs">
                  No elements yet. Use tools above to add elements.
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Brand Colors - Phase 6: Synced from Admin Settings */}
        <div className="p-3 border-t dark:border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-2">
              <Palette className="h-3 w-3" /> BRAND COLORS
            </Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0"
              onClick={refetchBrand}
              title="Refresh from Admin Settings"
              data-testid="refresh-brand-colors"
            >
              <RefreshCcw className={`h-3 w-3 ${brandLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {brandColors.map((color, idx) => (
              <button
                key={idx}
                className="w-6 h-6 rounded border border-slate-300 dark:border-slate-600 hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
                onClick={() => {
                  if (selectedId) {
                    updateElement(selectedId, { fill_color: color });
                  }
                }}
                title={color}
                data-testid={`brand-color-${idx}`}
              />
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Synced from Admin Settings</p>
        </div>
      </div>

      {/* Center - Canvas */}
      <div className="flex-1 flex flex-col" ref={containerRef}>
        {/* Top Toolbar */}
        <div className="h-12 bg-white dark:bg-slate-800 border-b dark:border-slate-700 flex items-center px-4 gap-2">
          <Button variant="ghost" size="sm" onClick={handleUndo} disabled={historyIndex <= 0}>
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleRedo} disabled={historyIndex >= history.length - 1}>
            <Redo2 className="h-4 w-4" />
          </Button>
          
          <Separator orientation="vertical" className="h-6" />
          
          <Button variant="ghost" size="sm" onClick={() => handleZoom(-0.1)}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs w-12 text-center">{Math.round(stageScale * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={() => handleZoom(0.1)}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          
          <Separator orientation="vertical" className="h-6" />
          
          <Button
            variant={showGrid ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setShowGrid(!showGrid)}
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
          
          <div className="flex-1" />
          
          {!readOnly && (
            <>
              <Button variant="outline" size="sm" onClick={handleSave} data-testid="save-design">
                <Save className="h-4 w-4 mr-2" /> Save
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-design">
                <Download className="h-4 w-4 mr-2" /> Export PNG
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleSchedule} data-testid="schedule-design">
                Schedule This
              </Button>
            </>
          )}
        </div>

        {/* Canvas Area */}
        <div className="flex-1 overflow-auto p-4 bg-slate-200 dark:bg-slate-900">
          <Stage
            ref={stageRef}
            width={canvasWidth * stageScale + 100}
            height={canvasHeight * stageScale + 100}
            scaleX={stageScale}
            scaleY={stageScale}
            x={stagePos.x}
            y={stagePos.y}
            onClick={handleStageClick}
            onTap={handleStageClick}
          >
            <Layer>
              {/* Canvas Background */}
              <Rect
                name="canvas-background"
                x={0}
                y={0}
                width={canvasWidth}
                height={canvasHeight}
                fill={canvasBackground}
                shadowColor="rgba(0,0,0,0.2)"
                shadowBlur={20}
                shadowOffset={{ x: 5, y: 5 }}
              />
              
              {/* Grid overlay */}
              {showGrid && (
                <>
                  {Array.from({ length: Math.ceil(canvasWidth / 50) }).map((_, i) => (
                    <Rect
                      key={`vg-${i}`}
                      x={i * 50}
                      y={0}
                      width={1}
                      height={canvasHeight}
                      fill="rgba(0,0,0,0.05)"
                    />
                  ))}
                  {Array.from({ length: Math.ceil(canvasHeight / 50) }).map((_, i) => (
                    <Rect
                      key={`hg-${i}`}
                      x={0}
                      y={i * 50}
                      width={canvasWidth}
                      height={1}
                      fill="rgba(0,0,0,0.05)"
                    />
                  ))}
                </>
              )}
              
              {/* Elements */}
              {elements.map(renderElement)}
              
              {/* Transformer */}
              <Transformer
                ref={transformerRef}
                boundBoxFunc={(oldBox, newBox) => {
                  if (newBox.width < 5 || newBox.height < 5) return oldBox;
                  return newBox;
                }}
                enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right', 'top-center', 'bottom-center']}
              />
            </Layer>
          </Stage>
        </div>
      </div>

      {/* Right Panel - Properties */}
      <div className="w-64 bg-white dark:bg-slate-800 border-l dark:border-slate-700 overflow-y-auto">
        {selectedElement ? (
          <div className="p-4 space-y-4">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">ELEMENT</Label>
              <Badge className="mt-1">{selectedElement.type}</Badge>
            </div>
            
            <Separator />
            
            {/* Position */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">POSITION</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">X</Label>
                  <Input
                    type="number"
                    value={Math.round(selectedElement.x)}
                    onChange={(e) => updateElement(selectedId, { x: Number(e.target.value) })}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Y</Label>
                  <Input
                    type="number"
                    value={Math.round(selectedElement.y)}
                    onChange={(e) => updateElement(selectedId, { y: Number(e.target.value) })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>
            
            {/* Size */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">SIZE</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Width</Label>
                  <Input
                    type="number"
                    value={Math.round(selectedElement.width)}
                    onChange={(e) => updateElement(selectedId, { width: Number(e.target.value) })}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Height</Label>
                  <Input
                    type="number"
                    value={Math.round(selectedElement.height || 50)}
                    onChange={(e) => updateElement(selectedId, { height: Number(e.target.value) })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>
            
            {/* Rotation */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">ROTATION</Label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[selectedElement.rotation || 0]}
                  min={0}
                  max={360}
                  step={1}
                  onValueChange={([v]) => updateElement(selectedId, { rotation: v })}
                  className="flex-1"
                />
                <span className="text-xs w-8">{Math.round(selectedElement.rotation || 0)}°</span>
              </div>
            </div>
            
            {/* Text Properties */}
            {selectedElement.type === 'text' && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">TEXT</Label>
                  <Input
                    value={selectedElement.text || ''}
                    onChange={(e) => updateElement(selectedId, { text: e.target.value })}
                    className="h-8 text-xs"
                    placeholder="Enter text"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Font</Label>
                      <select
                        value={selectedElement.font_family}
                        onChange={(e) => updateElement(selectedId, { font_family: e.target.value })}
                        className="w-full h-8 text-xs rounded border dark:bg-slate-700 dark:border-slate-600"
                      >
                        {FONT_OPTIONS.map(font => (
                          <option key={font} value={font}>{font}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Size</Label>
                      <Input
                        type="number"
                        value={selectedElement.font_size}
                        onChange={(e) => updateElement(selectedId, { font_size: Number(e.target.value) })}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant={selectedElement.font_weight === 'bold' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updateElement(selectedId, { 
                        font_weight: selectedElement.font_weight === 'bold' ? 'normal' : 'bold' 
                      })}
                    >
                      <Bold className="h-3 w-3" />
                    </Button>
                    <Button
                      variant={selectedElement.font_style === 'italic' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updateElement(selectedId, { 
                        font_style: selectedElement.font_style === 'italic' ? 'normal' : 'italic' 
                      })}
                    >
                      <Italic className="h-3 w-3" />
                    </Button>
                    <Separator orientation="vertical" className="h-8" />
                    <Button
                      variant={selectedElement.text_align === 'left' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updateElement(selectedId, { text_align: 'left' })}
                    >
                      <AlignLeft className="h-3 w-3" />
                    </Button>
                    <Button
                      variant={selectedElement.text_align === 'center' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updateElement(selectedId, { text_align: 'center' })}
                    >
                      <AlignCenter className="h-3 w-3" />
                    </Button>
                    <Button
                      variant={selectedElement.text_align === 'right' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => updateElement(selectedId, { text_align: 'right' })}
                    >
                      <AlignRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </>
            )}
            
            <Separator />
            
            {/* Color */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">FILL COLOR</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElement.fill_color || '#000000'}
                  onChange={(e) => updateElement(selectedId, { fill_color: e.target.value })}
                  className="w-8 h-8 rounded cursor-pointer"
                />
                <Input
                  value={selectedElement.fill_color || '#000000'}
                  onChange={(e) => updateElement(selectedId, { fill_color: e.target.value })}
                  className="h-8 text-xs flex-1"
                />
              </div>
            </div>
            
            {/* Opacity */}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">OPACITY</Label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[selectedElement.opacity * 100]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={([v]) => updateElement(selectedId, { opacity: v / 100 })}
                  className="flex-1"
                />
                <span className="text-xs w-8">{Math.round(selectedElement.opacity * 100)}%</span>
              </div>
            </div>
            
            <Separator />
            
            {/* Actions */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => duplicateElement(selectedId)}
              >
                <Copy className="h-3 w-3 mr-1" /> Duplicate
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => deleteElement(selectedId)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <Label className="text-xs font-medium text-muted-foreground">CANVAS</Label>
            
            {/* Canvas Background */}
            <div className="space-y-2">
              <Label className="text-xs">Background Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={canvasBackground}
                  onChange={(e) => setCanvasBackground(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer"
                />
                <Input
                  value={canvasBackground}
                  onChange={(e) => setCanvasBackground(e.target.value)}
                  className="h-8 text-xs flex-1"
                />
              </div>
            </div>
            
            {/* Canvas Size */}
            <div className="space-y-2">
              <Label className="text-xs">Canvas Size</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Width</Label>
                  <Input
                    type="number"
                    value={canvasWidth}
                    onChange={(e) => setCanvasWidth(Number(e.target.value))}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Height</Label>
                  <Input
                    type="number"
                    value={canvasHeight}
                    onChange={(e) => setCanvasHeight(Number(e.target.value))}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>
            
            {/* Presets */}
            <div className="space-y-2">
              <Label className="text-xs">Preset Sizes</Label>
              <div className="space-y-1">
                {Object.entries(DESIGN_PRESETS).map(([key, preset]) => (
                  <Button
                    key={key}
                    variant="outline"
                    size="sm"
                    className="w-full justify-between text-xs h-8"
                    onClick={() => {
                      setCanvasWidth(preset.width);
                      setCanvasHeight(preset.height);
                    }}
                  >
                    {preset.name}
                    <span className="text-muted-foreground">{preset.width}x{preset.height}</span>
                  </Button>
                ))}
              </div>
            </div>
            
            <Separator />
            
            <div className="text-center text-xs text-muted-foreground py-4">
              Select an element to edit its properties
            </div>
          </div>
        )}
      </div>
      
      {/* Asset Picker Modal */}
      <AssetPicker
        open={showAssetPicker}
        onClose={() => setShowAssetPicker(false)}
        onSelect={handleAssetSelect}
        filterType="image"
        title="Select Image from Library"
      />
    </div>
  );
};

// Image element component (handles async image loading)
const ImageElement = ({ src, ...props }) => {
  const [image, setImage] = useState(null);
  
  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    img.onload = () => setImage(img);
  }, [src]);
  
  if (!image) return null;
  
  return <KonvaImage image={image} {...props} />;
};

export default DesignEditor;
